import type { Product } from '@forest/native-services'
import {
  calculateMonthlyPrice,
  removeTrailingZeros,
} from '@forest/paywall-core'
import { cn } from '@forest/shared'
import { Show } from 'solid-js'
import { platform } from '~/lib/params'
import { m } from '~/paraglide/messages'
import { CheckedIcon } from './icons/checked'
import { UncheckedIcon } from './icons/unchecked'

interface PlanOptionProps {
  title: string
  earlyBirdProduct: Product
  originalProduct: Product
  selected: boolean
  plan: 'annual' | 'monthly'
  selectPlan: (plan: 'annual' | 'monthly') => void
}

export function PlanOption(props: PlanOptionProps) {
  const discountPercentage = () =>
    Math.round(
      ((props.originalProduct.price_value -
        props.earlyBirdProduct.price_value) /
        props.originalProduct.price_value) *
        100,
    )

  const monthlyBreakdown = () =>
    m.subscription_price_month({
      monthly_price:
        props.plan === 'annual'
          ? calculateMonthlyPrice({
              price_localized: props.earlyBirdProduct.price_localized,
              price_value: props.earlyBirdProduct.price_value,
            })
          : removeTrailingZeros(props.earlyBirdProduct.price_localized),
    })

  return (
    <button
      onClick={() => props.selectPlan(props.plan)}
      class={cn(
        'relative flex w-full items-center gap-4 rounded-2xl px-4 pb-3 pt-4',
      )}
      style={{
        background: props.selected
          ? 'linear-gradient(114deg, #EAFEBD 0%, #B2FCFF 100%) padding-box, linear-gradient(90deg, #81D200 0%, #0EBC73 47.5%, #08C1F3 87.5%) border-box'
          : 'linear-gradient(0deg, rgba(255, 255, 255, 0.50) 0%, rgba(255, 255, 255, 0.50) 100%) border-box, linear-gradient(114deg, #EAFEBD 1.4%, #B2FCFF 96.26%) border-box',
      }}
    >
      <Show when={props.selected}>
        <div
          class="absolute -inset-1 z-[-1] rounded-[20px]"
          style={{
            background: 'linear-gradient(90deg, #90E704 0%, #20D0FF 72%)',
          }}
        />
      </Show>

      <Show
        when={props.selected}
        fallback={<UncheckedIcon class="size-6 shrink-0" />}
      >
        <CheckedIcon class="size-6 shrink-0" />
      </Show>

      <div class="flex shrink-0 flex-col items-start gap-1 text-left">
        <p class="font-semibold text-[#035462]">{props.title}</p>
        <div class="flex w-full items-center gap-1">
          <p class="text-lg font-semibold text-[#333]">
            {platform === 'china_android'
              ? m.subscription_price_early_bird({
                  price: removeTrailingZeros(
                    props.earlyBirdProduct.price_localized,
                  ),
                })
              : removeTrailingZeros(props.earlyBirdProduct.price_localized)}
          </p>
          <p class="text-sm text-[#333333] line-through opacity-50">
            {removeTrailingZeros(props.originalProduct.price_localized)}
          </p>
          <p class="h-[22px] rounded-[20px] bg-[#063639] px-2 text-sm text-[#E1FAF1]">
            -{discountPercentage()}%
          </p>
        </div>
        <Show when={monthlyBreakdown()}>
          <p class="w-full font-semibold text-[#666666]">
            {monthlyBreakdown()}
          </p>
        </Show>
      </div>

      <Show when={(props.earlyBirdProduct.eligible_trial_days ?? 0) > 0}>
        <div class="absolute right-0 top-0 flex h-7 items-center rounded-bl-[16px] rounded-tr-[16px] bg-[#48D8FF] px-3 text-sm font-medium text-[#063639]">
          {m.tag_trial_day({
            free_trial_day: props.earlyBirdProduct.eligible_trial_days!,
          })}
        </div>
      </Show>
    </button>
  )
}
